import Events from './Events';
import Request, { Options } from "live-page/request";

export default class Dialog extends EventTarget
{
    #html;
    #closing = false;
    #opening = false;
    #loading = false;

    constructor(element)
    {
        super();

        this.modifiers = [];

        this.opened = false;
        this.target = element;
        this.content = this.target.querySelector('.dialog__content');
        this.closeCallback = null;
        this.closedCallback = null;
        this.scriptQueue = [];
        this.scrollableElement = this.content;
        this.locked = false;
        this.bind();
    }

    bind() {
        this.target.addEventListener('close', this.onClose.bind(this));

        this.target.addEventListener("transitioncancel", this.onTransitionEnd.bind(this));
        this.target.addEventListener("transitionend", this.onTransitionEnd.bind(this));

        Events.delegate(this.target, 'click', '.d3r-hide-trigger[data-target="overlay"],[dialog-close]', (e) => {
            e.preventDefault();
            this.close();
        });
    }

    render(config)
    {
        this.open(config, {"config": config});
    }

    load(url, config) {
        this.#html = '';

        const options = new Options(url);
        options.method = config.method;

        if (options.method == 'POST') {
            options.body = config.data;
        } else if (config.data) {
            let params = new URLSearchParams(config.data);
            options.url += '?' + params.toString();
        }

        const request = new Request(options);

        this.loading = true;
        this.dispatchEvent(new Event(Events.LOADING, {bubbles: true}));

        if (config.response_type === 'html') {
            const fetchOptions = options.getFetchConfig();
            fetchOptions.headers['Accept'] = 'text/html';
            fetch(options.url, fetchOptions)
                .then(response => {
                    if (!response.ok) {
                        throw response;
                    }
                    return response.text();
                }).then(html => {
                    this.response(html, config);
                }).catch((response) => {
                    this.error(response, config);
                });
        } else {
            request.fire(async (json) => {
                await this.response(json, config);
            }).catch((response) => {
                console.error('Request failed', response);
                this.error(response, config);
            });
        }

        this.target.classList.toggle('dialog--loading-template', config.loading_template ? true : false);
        this.target.classList.toggle('dialog--loading-active', config.loading === 'active');

        if (config.loading === 'active') {
            this.addLoadingContent(config, {"config": config});
            this.open(config, {"config": config});
        }
    }

    error(response, config) {
        this.loading = false;
        if (response.status == 401) {
            window.location.href = '/account/login?redirect=' + encodeURIComponent(window.location.pathname);
            return;
        }
        this.html = '<p class="dialog__error">An error occurred while loading. Please try again later.</p>';
        this.addContent(config, {"config": config, response: response});
        if (config.loading !== 'active') {
            this.open(config, {"config": config, response: response});
        }
    }

    /**
     * Start opening a new dialog. This can be called if one is already open
     *
     * @param {object} config
     * @param {array} eventData
     */
    open(config, eventData) {
        // Safely parse the response, or default to an empty object
        var data;
        if (typeof eventData !== 'undefined' && eventData.response) {
            data = eventData.response;
        } else {
            data = {};
        }

        var pageTitle = 'Modal'; // Default title
        if (data.payload && typeof data.payload.page_title === 'string') {
            pageTitle = data.payload.page_title;
        }

        this.setTitle(pageTitle);

        if (config.cross_transition) {
            this.cross = true;
        }

        this.openTransition(config, eventData);
    }

    /**
     * This is where the content is actually replaced
     *
     * @param {object} config
     * @param {string|function|Element} content
     * @param {Array} eventData
     */
    openTransition(config, eventData) {
        if (config.loading === 'background') {
            this.addContent(config, eventData);
        }

        for (var i = 0; i < this.modifiers.length; i++) {
            var modifier = this.modifiers[i];
            this.target.classList.remove('dialog--' + modifier);
        }

        for (var i = 0; i < config.modifiers.length; i++) {
            var modifier = config.modifiers[i];
            this.modifiers.push(modifier);
            this.target.classList.add('dialog--' + modifier);
        }

        this.removeTranstionClasses();

        if (config.transition) {
            this.target.classList.add('dialog-transition--' + config.transition);
        }

        this.closeCallback = config.close;
        this.closedCallback = config.closed;

        this.scrollableElement = 'function' === typeof config.scrollable_element
            ? config.scrollable_element(this)
            : (config.scrollable_element ?? this.scrollableElement);

        this.opened = true;

        this.dispatchEvent(new Event(Events.OPEN, {bubbles: true}));

        this.target.addEventListener('transitionrun', (e) => {
            this.target.classList.add('dialog--opening');
            this.opening = true;
        }, {once: true});

        this.target.showModal();

        if (!this.isDynamic() || config.loading === 'background') {
            config.callback.call(this, eventData);
        }

        // No transition fallback
        setTimeout((e) => {
            if (!this.opening) {
                this.closing = false;
                this.target.classList.remove('dialog--cross');
                this.dispatchEvent(new Event(Events.OPENED, {bubbles: true}));
            }
        }, 100)
    }

    /**
     * Handle a raw ajax response that we assume is going to be a json in a specific fomat
     *
     *  {
     *      "payload": {
     *          "html": "<div>blah</div>",
     *          "scripts": [
     *              {
     *                  "url": "/javascript/example.js",
     *                  "attributes": {}
     *              }
     *          ],
     *          "stylesheets": [
     *              {
     *                  "url": "/css/example.css",
     *                  "media": "screen"
     *              }
     *          ],
     *      }
     *  }
     *
     * @param {object} raw
     * @param {object} config
     */
    async response(raw, config) {
        if (config.response_type == 'html') {
            this.html = raw;
            this.open(config, {"config": config});
            return;
        }

        const content = raw.payload.html;

        this.html = content;
        if (config.loading === 'active') {
            this.addContent(config, {"config": config, "response": raw});
            await config.callback.call(this, {"config": config, "response": raw});
        } else {
            this.open(config, {"config": config, "response": raw});
        }
        this.loading = false;
        this.dispatchEvent(new Event(Events.LOADED, {bubbles: true}));
    }

    /**
     * Close the dialog, calling the passed method when complete
     *
     * @param {callable} callback
     */
    close(callback) {
        this.addEventListener(Events.CLOSED, () => {
            if (callback) {
                callback.call(this);
            }
        }, {once: true});
        this.target.close();
    }

    onClose(e) {
        this.closing = true;

        this.dispatchEvent(new Event(Events.CLOSE, {bubbles: true}));

        if (this.closeCallback) {
            this.closeCallback.call(this);
        }

        // No transition
        if (getComputedStyle(e.target).display === 'none') {
            this.closing = false;
            this.opened = false;

            this.html = '';

            if (this.closedCallback) {
                this.closedCallback.call(this);
            }
            this.dispatchEvent(new Event(Events.CLOSED, {bubbles: true}));
        }
    }

    onTransitionEnd(e) {
        if (e.pseudoElement || e.target.tagName !== 'DIALOG') {
            return;
        }
        if (this.closing) {
            this.closing = false;
            this.opened = false;
            this.opening = false;

            this.html = '';

            if (this.closedCallback) {
                this.closedCallback.call(this);
            }
            this.dispatchEvent(new Event(Events.CLOSED, {bubbles: true}));
        } else {
            this.closing = false;
            this.opening = false;
            this.target.classList.remove('dialog--cross');
            this.dispatchEvent(new Event(Events.OPENED, {bubbles: true}));
        }
    }


    /**
     * Add a stylesheet to the page
     *
     * @param {string} url
     * @param {object} media
     * @memberof Editables
     */
    addStylesheet(url, attributes, config, defer)
    {
        if (!document.querySelector('link[href="' + url + '"]')) {
            let link = document.createElement('link');
            link.setAttribute('href', url);
            for (const key in attributes) {
                link.setAttribute(key, attributes[key]);
            }

            if (defer) {
                link.addEventListener('load', function(e) {
                    defer.resolve();
                });
            }

            document.head.appendChild(link);
        } else if (defer) {
            defer.resolve();
        }
    }

    /**
     * Get the list of focusable elements
     *
     * @returns {Array}
     */
    getFocusableElements() {
        const nodes = this.content.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const focusableElements = [];

        for (let i = 0; i < nodes.length; i++) {
            focusableElements.push(nodes[i]);
        }

        const dialogCloseButton = document.querySelector('button.dialog__close');

        if (dialogCloseButton) {
            focusableElements.unshift(dialogCloseButton);
        }

        return focusableElements;
    }

    addLoadingContent(config, eventData) {
        if (config.loading_template) {
            const selected = document.querySelector(config.loading_template);
            if (selected) {
                this.content.innerHTML = '';
                this.content.appendChild(selected.content.cloneNode(true));
            }
        } else if (config.loading_content) {
            this.content.innerHTML = config.loading_content;
        }
    }

    addContent(config, eventData) {
        if (this.html) {
            if (this.content.querySelector('[dialog-content]')) {
                this.content.querySelector('[dialog-content]').innerHTML = this.html;
            } else if (window.Idiomorph !== undefined) {
                Idiomorph.morph(this.content, this.html, {morphStyle:'innerHTML'});
            } else {
                this.content.innerHTML = this.html;
            }
        }

        if (config.eval_scripts) {
            var scripts = this.content.querySelectorAll('script');
            scripts.forEach((script, i) => {
                Function(script.innerHTML)();
            });
        }
    }

    /**
     * Add script to the page if it isn't already on
     *
     * @param {string} url
     * @param {object} attributes
     * @memberof Editables
     */
    addScript(url, attributes, config, defer)
    {
        if (!document.querySelector('script[src="' + url + '"]')) {
            let script = document.createElement('script');
            script.setAttribute('src', url);
            for (const key in attributes) {
                script.setAttribute(key, attributes[key]);
            }
            script.addEventListener('load', function(e) {
                config.script_callback.call(this, url);
                if (defer) {
                    defer.resolve();
                }
            });
            document.body.appendChild(script);
        } else {
            config.script_callback.call(this, url);
            if (defer) {
                defer.resolve();
            }
        }
    }

    removeTranstionClasses() {
        for (let i = 0; i < this.target.classList.length; i++) {
            var cssClass = this.target.classList[i];
            if (cssClass.indexOf('dialog-transition--') === 0) {
                this.target.classList.remove(cssClass);
            }
        }
    }

    set cross(bool)
    {
        this.target.classList.toggle('dialog--cross', bool);
    }

    get cross() {
        return this.target.classList.contains('dialog--cross');
    }

    /**
     * Sets the `aria-label` attribute of the target element.
     *
     * @param {string} title The text to be set as the value of the `aria-label` attribute.
     */
    setTitle(title) {
        this.target.setAttribute('aria-label', title);
    }

    isDynamic() {
        return this.target.hasAttribute('dynamic');
    }

    destroy() {
        this.target.removeEventListener('close', this.onClose.bind(this));
        this.target.removeEventListener("transitioncancel", this.onTransitionEnd.bind(this));
        this.target.removeEventListener("transitionend", this.onTransitionEnd.bind(this));

        if (this.target.parentNode) {
            this.target.parentNode.removeChild(this.target);
        }

        this.dispatchEvent(new Event(Events.DESTROYED, {bubbles: true}));
    }

    set loading(value) {
        this.#loading = value;
        this.target.classList.toggle('dialog--loading', value);
    }

    get loading() {
        return this.#loading;
    }

    set html(html) {
        this.#html = html;
    }

    get html() {
        return this.#html;
    }

    set closing(bool) {
        this.#closing = bool;
        this.target.classList.toggle('dialog--closing', bool);
    }

    get closing() {
        return this.#closing;
    }

    set opening(bool) {
        this.#opening = bool;
        this.target.classList.toggle('dialog--opening', bool);
    }

    get opening() {
        return this.#opening;
    }
}
